import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Package,
  ShoppingCart,
  Users,
  DollarSign,
  TrendingUp,
  AlertCircle,
  Plus,
  FileText,
  Mail,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'
import Button from '../../components/ui/Button'

export default function AdminDashboard() {
  const { user, isAuthenticated, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    lowStockProducts: 0,
  })
  const [recentOrders, setRecentOrders] = useState([])
  const [lowStockItems, setLowStockItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/account')
      return
    }
    if (!isAdmin) {
      navigate('/')
      return
    }
    fetchDashboardData()
  }, [isAuthenticated, isAdmin])

  const fetchDashboardData = async () => {
    try {
      // Fetch products count
      const { count: productCount } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })

      // Fetch orders
      const { data: orders, count: orderCount } = await supabase
        .from('orders')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .limit(5)

      // Calculate total revenue
      const { data: revenueData } = await supabase
        .from('orders')
        .select('total')
        .eq('payment_status', 'paid')

      const totalRevenue = revenueData?.reduce((sum, order) => sum + order.total, 0) || 0

      // Fetch low stock products
      const { data: lowStock } = await supabase
        .from('products')
        .select('*')
        .lt('stock_quantity', 10)
        .order('stock_quantity', { ascending: true })

      setStats({
        totalProducts: productCount || 0,
        totalOrders: orderCount || 0,
        totalRevenue,
        lowStockProducts: lowStock?.length || 0,
      })

      setRecentOrders(orders || [])
      setLowStockItems(lowStock || [])
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(price)
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const statCards = [
    {
      title: 'Total Products',
      value: stats.totalProducts,
      icon: Package,
      color: 'bg-pp-sage/10 text-pp-sage',
    },
    {
      title: 'Total Orders',
      value: stats.totalOrders,
      icon: ShoppingCart,
      color: 'bg-pp-terracotta/10 text-pp-terracotta',
    },
    {
      title: 'Revenue',
      value: formatPrice(stats.totalRevenue),
      icon: DollarSign,
      color: 'bg-pp-butter/20 text-pp-butter',
    },
    {
      title: 'Low Stock Items',
      value: stats.lowStockProducts,
      icon: AlertCircle,
      color: stats.lowStockProducts > 0 ? 'bg-red-100 text-red-600' : 'bg-pp-sand/50 text-pp-stone',
    },
  ]

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading dashboard...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-pp-linen">
      <div className="container mx-auto px-4 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-display-md text-pp-ink">
              Admin Dashboard
            </h1>
            <p className="text-pp-stone">Manage your products, orders, and inventory</p>
          </div>
          <Button as={Link} to="/admin/products/new">
            <Plus className="w-4 h-4" />
            Add Product
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 border border-pp-sand/50"
            >
              <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center mb-4`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <p className="text-pp-stone text-sm mb-1">{stat.title}</p>
              <p className="font-display text-2xl text-pp-ink">{stat.value}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Recent Orders */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-pp-sand/50"
          >
            <div className="p-6 border-b border-pp-sand/50 flex items-center justify-between">
              <h2 className="font-display text-xl text-pp-ink">Recent Orders</h2>
              <Link
                to="/admin/orders"
                className="text-sm text-pp-terracotta hover:underline"
              >
                View all
              </Link>
            </div>
            <div className="p-6">
              {recentOrders.length === 0 ? (
                <p className="text-pp-stone text-center py-8">No orders yet</p>
              ) : (
                <div className="space-y-4">
                  {recentOrders.map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between py-3 border-b border-pp-sand/30 last:border-0"
                    >
                      <div>
                        <p className="font-medium text-pp-ink">
                          {order.order_number}
                        </p>
                        <p className="text-sm text-pp-stone">
                          {order.customer_email}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-pp-ink">
                          {formatPrice(order.total)}
                        </p>
                        <p className="text-xs text-pp-stone">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Low Stock Alert */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl border border-pp-sand/50"
          >
            <div className="p-6 border-b border-pp-sand/50 flex items-center justify-between">
              <h2 className="font-display text-xl text-pp-ink">Low Stock Alert</h2>
              <Link
                to="/admin/products"
                className="text-sm text-pp-terracotta hover:underline"
              >
                Manage stock
              </Link>
            </div>
            <div className="p-6">
              {lowStockItems.length === 0 ? (
                <p className="text-pp-stone text-center py-8">
                  All products are well stocked
                </p>
              ) : (
                <div className="space-y-4">
                  {lowStockItems.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between py-3 border-b border-pp-sand/30 last:border-0"
                    >
                      <div>
                        <p className="font-medium text-pp-ink">{product.name}</p>
                        <p className="text-sm text-pp-stone">{product.category}</p>
                      </div>
                      <div className="text-right">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            product.stock_quantity === 0
                              ? 'bg-red-100 text-red-600'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {product.stock_quantity} left
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          <Link
            to="/admin/products"
            className="bg-white rounded-2xl p-6 border border-pp-sand/50 hover:border-pp-terracotta/50 transition-colors group"
          >
            <Package className="w-8 h-8 text-pp-sage mb-4" />
            <h3 className="font-display text-lg text-pp-ink group-hover:text-pp-terracotta transition-colors">
              Manage Products
            </h3>
            <p className="text-sm text-pp-stone mt-1">
              Add, edit, or remove products
            </p>
          </Link>

          <Link
            to="/admin/orders"
            className="bg-white rounded-2xl p-6 border border-pp-sand/50 hover:border-pp-terracotta/50 transition-colors group"
          >
            <ShoppingCart className="w-8 h-8 text-pp-terracotta mb-4" />
            <h3 className="font-display text-lg text-pp-ink group-hover:text-pp-terracotta transition-colors">
              View Orders
            </h3>
            <p className="text-sm text-pp-stone mt-1">
              Track and manage customer orders
            </p>
          </Link>

          <Link
            to="/admin/pages"
            className="bg-white rounded-2xl p-6 border border-pp-sand/50 hover:border-pp-terracotta/50 transition-colors group"
          >
            <FileText className="w-8 h-8 text-pp-butter mb-4" />
            <h3 className="font-display text-lg text-pp-ink group-hover:text-pp-terracotta transition-colors">
              Page Content
            </h3>
            <p className="text-sm text-pp-stone mt-1">
              Edit website pages and images
            </p>
          </Link>

          <Link
            to="/admin/enquiries"
            className="bg-white rounded-2xl p-6 border border-pp-sand/50 hover:border-pp-terracotta/50 transition-colors group"
          >
            <Mail className="w-8 h-8 text-pp-sage mb-4" />
            <h3 className="font-display text-lg text-pp-ink group-hover:text-pp-terracotta transition-colors">
              Enquiries
            </h3>
            <p className="text-sm text-pp-stone mt-1">
              View contact form messages
            </p>
          </Link>
        </div>
      </div>
    </div>
  )
}
